/**
 * Text normalization utilities for accent-insensitive search and comparison.
 * Matches backend PostgreSQL unaccent() behavior for consistency.
 */

/**
 * Removes accents/diacritics from text using Unicode normalization.
 * This matches the backend PostgreSQL unaccent() function behavior.
 *
 * @example
 * ```ts
 * removeAccents('João Silva')     // → 'Joao Silva'
 * removeAccents('María Santos')   // → 'Maria Santos'
 * removeAccents('São Paulo')      // → 'Sao Paulo'
 * removeAccents('Renée')          // → 'Renee'
 * ```
 *
 * **How it works:**
 * 1. normalize('NFD') decomposes characters into base + diacritical marks
 *    - 'João' → 'Joa\u0303o' (o + tilde mark)
 * 2. replace(/[\u0300-\u036f]/g, '') removes all diacritical marks
 *    - 'Joa\u0303o' → 'Joao'
 *
 * @param text - Text to remove accents from
 * @returns Text without accents
 */
export function removeAccents(text: string): string {
  if (!text) return '';

  return text
    .normalize('NFD') // Decompose into base characters + combining marks
    .replace(/[\u0300-\u036f]/g, ''); // Remove combining diacritical marks
}

/**
 * Normalizes text for case-insensitive, accent-insensitive comparison.
 * Use this for client-side filtering and search.
 *
 * @example
 * ```ts
 * normalizeForSearch('João Silva')  // → 'joao silva'
 * normalizeForSearch('  MARÍA  ')   // → 'maria'
 * ```
 *
 * @param text - Text to normalize
 * @returns Normalized text (lowercase, no accents, trimmed)
 */
export function normalizeForSearch(text: string): string {
  if (!text) return '';

  return removeAccents(text).toLowerCase().trim();
}

/**
 * Checks if text contains search term (accent & case insensitive).
 * Use this for client-side filtering (tabs, QuickSearchBar, etc.).
 *
 * @example
 * ```ts
 * searchIncludes('João Silva', 'joao')     // → true
 * searchIncludes('João Silva', 'silva')    // → true
 * searchIncludes('Maria Santos', 'maria')  // → true
 * searchIncludes('São Paulo', 'sao')       // → true
 * searchIncludes('João Silva', 'pedro')    // → false
 * ```
 *
 * **Why this is needed:**
 * - Backend uses PostgreSQL unaccent() for server-side search
 * - Frontend needs matching behavior for client-side filtering
 * - Ensures consistency: "Joao" finds "João" on both sides
 *
 * @param text - Text to search in
 * @param searchTerm - Term to search for
 * @returns True if text contains search term (normalized)
 */
export function searchIncludes(text: string, searchTerm: string): boolean {
  if (!text || !searchTerm) return false;

  return normalizeForSearch(text).includes(normalizeForSearch(searchTerm));
}

/**
 * Checks if multiple texts match search term (any match returns true).
 * Useful for searching across multiple fields.
 *
 * @example
 * ```ts
 * const person = { firstName: 'João', lastName: 'Silva', email: 'joao@example.com' };
 * searchIncludesAny(['João Silva', 'joao@example.com'], 'silva')  // → true
 * searchIncludesAny(['João Silva', 'joao@example.com'], 'maria')  // → false
 * ```
 *
 * @param texts - Array of texts to search in
 * @param searchTerm - Term to search for
 * @returns True if any text contains search term
 */
export function searchIncludesAny(texts: string[], searchTerm: string): boolean {
  if (!searchTerm) return false;

  return texts.some(text => searchIncludes(text, searchTerm));
}
